import React, { useContext, useState } from 'react'
import { BigNumber } from 'ethers'
import './SingleFarm.scss'
import { ArcContext } from 'src/contexts/ArcContext'
import { formatInputValue } from 'src/utils/format/formatInputValue'
import { CloseIcon, ExternalLinkIcon } from 'src/assets/images'
import Loader from 'react-loader-spinner'
import { getReadableNumber } from 'src/helpers/getReadableNumber'
import { useWeb3React } from '@web3-react/core'
import { TestTokenFactory } from '@arcxgame/contracts/dist/src/typings'
import { parseEther } from 'ethers/lib/utils'
import ReactTooltip from 'react-tooltip'
import { CustomInfoTooltip } from '../CustomInfoTooltip'
import { useSingleFarmDetails } from './useSingleFarmDetails'
import { useHistory } from 'react-router-dom'

interface IProps {
  farmName: string
}

export function SingleFarm({ farmName }: IProps) {
  const history = useHistory()

  const {
    note,
    pairLink,
    pair,
    rewardTokens,
    stakedBalance,
    endDates,
    earnings,
    debtDeadline,
    debtRequirement,
    currentDebt,
    availableToStake,
    stakingSymbol,
    stakableBalance,
    tokensClaimable,
    farm,
    hasDebtRequirement,
  } = useSingleFarmDetails(farmName)

  const { account, chainId } = useWeb3React()
  const { arc, isLoading, setIsLoading, notifyInstance } = useContext(ArcContext)

  const [inputValue, setInputValue] = useState<BigNumber>(BigNumber.from(0))
  const [inputValueStr, setInputValueStr] = useState<string>('0')

  const farmNumber = farmName.split('-')[1]

  const onInputValueChanged = (event) => {
    const formattedInputValue = formatInputValue(event.target.value)

    if (formattedInputValue) {
      setInputValue(formattedInputValue.inputValue)
      setInputValueStr(formattedInputValue.inputValueStr)
    }
  }

  const onMaxBtnClicked = () => {
    setInputValue(availableToStake)
    setInputValueStr(getReadableNumber(availableToStake, 6))
  }

  const onStakeClicked = async () => {
    try {
      setIsLoading(true)

      const stakeTx = await farm.stake(inputValue)
      await stakeTx.wait()
    } catch (err) {
      console.error('On stake clicked:')
      console.error(err)
      notifyInstance.notification({
        type: 'error',
        message: err.message,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const onMintClicked = async () => {
    try {
      setIsLoading(true)
      const stakingToken = await farm.getStakingToken()
      const token = TestTokenFactory.connect(stakingToken.address, arc.signer)
      const mintTx = await token.mintShare(account, parseEther('100'))
      await mintTx.wait()
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  const onExitClicked = async () => {
    try {
      setIsLoading(true)
      // Fix for broken Farm 6.x
      if (!tokensClaimable || farmNumber.startsWith('6')) {
        const withdrawTx = await farm.withdraw(stakedBalance)
        await withdrawTx.wait()
        return
      }
      const exitTx = await farm.exit()
      await exitTx.wait()
    } catch (err) {
      console.error('On exit clicked:')
      console.error(err)
      if (err.message?.includes('Tokens cannot be claimed yet')) {
        const withdrawTx = await farm.withdraw(stakedBalance)
        await withdrawTx.wait()
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onClaimClicked = async () => {
    try {
      setIsLoading(true)

      const rewardTx = await farm.claimReward()
      await rewardTx.wait()
    } catch (err) {
      console.error('on stake clicked', err)
      notifyInstance.notification({
        type: 'error',
        message:
          'ARCX token are not claimable yet. If you think this is not right, let us know in our Discord',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const onCloseBtnClicked = () => {
    history.push('/farms')
  }

  return (
    <div className="single-farm">
      <ReactTooltip multiline id="single-farm" />
      <div className="single-farm__info-subtitle-container">
        {/* <div> */}
        <span className="single-farm__note" dangerouslySetInnerHTML={{ __html: note }} />
        {/* <span className='single-farm__info-subtitle-tooltip'>
            <CustomInfoTooltip
              forId='single-farm'
              place='top'
              // TODO tooltip
              description='TODO tooltip'
            />
          </span> */}
        {/* </div> */}
        <button className="single-farm__close-btn" onClick={onCloseBtnClicked}>
          <img src={CloseIcon} alt="close" />
        </button>
      </div>
      <h1>Farm {farmNumber}</h1>
      <div className="single-farm__info-container">
        <div className="single-farm__info-line">
          <span>Pair</span>
          <span className="single-farm__info-value">
            {pairLink ? (
              <a
                className="single-farm__pair-ext-link"
                href={pairLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={ExternalLinkIcon} alt="open external link" />
                <span>{pair}</span>
              </a>
            ) : (
              <span>{pair}</span>
            )}
          </span>
        </div>
        <div className="single-farm__info-line">
          <span>Pair wallet balance</span>
          <span className="single-farm__info-value">{getReadableNumber(stakableBalance)}</span>
        </div>
        <div className="single-farm__info-line">
          <span>Farm token</span>
          <span className="single-farm__info-value">{rewardTokens}</span>
        </div>
        <div className="single-farm__info-line">
          <span className="single-farm__info-title">
            <span>Staked Balance</span>
            <span className="single-farm__info-subtitle-tooltip">
              <CustomInfoTooltip
                forId="single-farm"
                place="top"
                description={`The amount of ${pair} you staked in this pool.`}
              />
            </span>
          </span>
          <span className="single-farm__info-value">{`${getReadableNumber(stakedBalance, 6)} ${
            stakingSymbol || ''
          }`}</span>
        </div>
        {endDates?.map((endDatePair) => (
          <div className="single-farm__info-line" key={endDatePair.symbol}>
            <span className="single-farm__info-title">
              <span>{endDates.length === 1 ? 'Farm' : endDatePair.symbol} end date</span>
              <span className="single-farm__info-subtitle-tooltip">
                <CustomInfoTooltip
                  forId="single-farm"
                  place="top"
                  description="When the rewards finish being distributed."
                />
              </span>
            </span>
            <span className="single-farm__info-value">{endDatePair.endDateString}</span>
          </div>
        ))}
        {earnings?.map((earnedPair) => (
          <div className="single-farm__info-line" key={earnedPair.symbol}>
            <span className="single-farm__info-title">
              <span>{earnedPair.symbol} Earned</span>
              <span className="single-farm__info-subtitle-tooltip">
                <CustomInfoTooltip
                  forId="single-farm"
                  place="top"
                  description={`Amount of ${earnedPair.symbol} you have earned.`}
                />
              </span>
            </span>
            <span className="single-farm__info-value">{getReadableNumber(earnedPair.amount)}</span>
          </div>
        ))}
        {debtDeadline && (
          <div className="single-farm__info-line">
            <span className="single-farm__info-title">
              <span>Debt deadline</span>
              <span className="single-farm__info-subtitle-tooltip">
                <CustomInfoTooltip
                  forId="single-farm"
                  place="top"
                  description="Deadline for how long you need to keep your vault open."
                />
              </span>
            </span>
            <span className="single-farm__info-value">{debtDeadline}</span>
          </div>
        )}
        {debtRequirement && (
          <div className="single-farm__info-line">
            <span className="single-farm__info-title">
              <span>Debt requirements</span>
              <span className="single-farm__info-subtitle-tooltip">
                <CustomInfoTooltip
                  forId="single-farm"
                  place="top"
                  description={
                    'How much STABLEx debt you need to maintain until the debt deadline to not get slashed.'
                  }
                />
              </span>
            </span>
            <span className="single-farm__info-value">{debtRequirement}</span>
          </div>
        )}
        {hasDebtRequirement && (
          <div className="single-farm__info-line">
            <span className="single-farm__info-title">
              <span>Current Debt</span>
              <span className="single-farm__info-subtitle-tooltip">
                <ReactTooltip multiline id="current-debt" />
                <CustomInfoTooltip
                  forId="current-debt"
                  place="top"
                  description={
                    'The highest current debt of STABLEx you have minted in a single vault.'
                  }
                />
              </span>
            </span>
            <span className="single-farm__info-value">{currentDebt}</span>
          </div>
        )}
      </div>
      <div className="single-farm__available-container">
        <span>Available to stake</span>
        <span className="single-farm__info-value">{getReadableNumber(availableToStake, 6)}</span>
      </div>
      <div className="single-farm__input-container">
        <div className="single-farm__input-token">{stakingSymbol}</div>
        <input
          className="single-farm__input"
          type="number"
          value={inputValueStr}
          onChange={onInputValueChanged}
        />
        <button className="single-farm__max-btn" onClick={onMaxBtnClicked}>
          MAX
        </button>
      </div>
      <div
        data-for="single-farm"
        data-effect="solid"
        data-place="top"
        data-tip={inputValue.eq(BigNumber.from(0)) ? 'The amount to stake must be above zero' : ''}
        className="single-farm__stake-btn-container"
      >
        <button
          className="single-farm__stake-btn"
          onClick={onStakeClicked}
          disabled={
            isLoading ||
            inputValue.eq(BigNumber.from(0)) ||
            inputValue.gt(availableToStake) ||
            farm.name === 'Pool-6'
          }
        >
          {isLoading ? <Loader type="Grid" color="white" height={25} /> : 'Press to stake'}
        </button>
      </div>
      {chainId !== 1 && (
        <button className="single-farm__stake-btn" onClick={onMintClicked} disabled={isLoading}>
          {isLoading ? (
            <Loader type="Grid" color="white" height={25} />
          ) : (
            'Press to Mint Test Tokens'
          )}
        </button>
      )}
      <div className="single-farm__bottom-action-buttons">
        <button
          className="single-farm__action-btn"
          onClick={onExitClicked}
          disabled={isLoading || stakedBalance?.eq(BigNumber.from(0))}
        >
          {isLoading ? (
            <Loader type="Grid" color="white" height={25} />
          ) : tokensClaimable ? (
            'Exit farm'
          ) : (
            'Withdraw staked tokens'
          )}
        </button>
        <button
          className="single-farm__action-btn single-farm__action-btn--green"
          onClick={onClaimClicked}
          disabled={isLoading || !tokensClaimable}
        >
          {isLoading ? <Loader type="Grid" color="white" height={25} /> : 'Claim tokens'}
        </button>
      </div>
    </div>
  )
}
